import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { FunctionComponent, useState } from "react";
import { Button } from "src/components/generic";
import { stripe } from "src/utils";

interface ISubscriberCheckoutFormProps {
  customerId: string;
  priceId: string;
  planName: string;
  billingCycle: string; // e.g., "Monthly"
  firstPaymentAmount: number; // in cents
  included: string[];
  onSubscriptionSuccess: () => void;
}

const SubscriberCheckoutForm: FunctionComponent<
  ISubscriberCheckoutFormProps
> = ({
  customerId,
  priceId,
  planName,
  billingCycle,
  firstPaymentAmount,
  included,
  onSubscriptionSuccess,
}) => {
  const stripeConn = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formattedAmount = `$${(firstPaymentAmount / 100).toFixed(2)}`;

  const submitPayment = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripeConn || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Confirm SetupIntent to save payment method for subscription
      const { error: setupError, setupIntent } = await stripeConn.confirmSetup({
        elements,
        redirect: "if_required",
      });

      if (setupError) {
        setError(setupError.message || "Failed to confirm payment method");
        setLoading(false);
        return;
      }

      if (setupIntent?.status === "succeeded" && setupIntent?.payment_method) {
        // Send to backend to create subscription
        console.log(setupIntent?.payment_method);
        const res = await stripe.payments.subscribeUser(
          customerId,
          priceId,
          typeof setupIntent?.payment_method === "string"
            ? setupIntent?.payment_method
            : `${setupIntent?.payment_method}`
        );
        console.log({res});
        if (res.subscription.id) {
          onSubscriptionSuccess();
        } else {
          setError("Subscription creation failed");
        }
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    }

    setLoading(false);
  };

  return (
    <div className="flex justify-between w-full">
      <div className="w-[45%]">
        <PaymentElement
          id="payment-element"
          options={{ layout: "accordion" }}
        />
      </div>

      <div className="ml-6">
        <div className="bg-gradient-to-br from-primary/90 to-primary/50 text-white p-8 rounded-2xl shadow-xl border border-white/10 backdrop-blur-md space-y-6 animate-fadeIn">
          <div className="space-y-2">
            <h3 className="text-2xl font-semibold tracking-wide text-white">
              Subscribing to {planName}
            </h3>
            <p className="text-white/80 leading-relaxed">
              This subscription is billed {billingCycle.toLowerCase()}. Your
              first payment of{" "}
              <span className="font-medium">{formattedAmount}</span> will be
              charged now.
            </p>
            <p className="text-white/80 leading-relaxed">
              Cancelling the subscription will require the rest of the yearly payments to be paid in full.
            </p>
            <p className="text-white/70 text-sm">
              By subscribing, you accept{" "}
              <span className="text-secondary-light hover:text-white transition-colors cursor-pointer">
                ClientVitals' Terms & Agreements
              </span>{" "}
              and{" "}
              <span className="text-secondary-light hover:text-white transition-colors cursor-pointer">
                Privacy Policy
              </span>
              .
            </p>
          </div>

          <div className="border-t border-white/10 pt-4 space-y-2">
            <p className="font-semibold text-lg text-secondary-light">
              {planName} ({billingCycle})
            </p>
            <p className="font-semibold text-md text-secondary-light">
              What's Included:
            </p>
            {
              included.map(service => (
                <p key={service} className="text-white/90">
                  {service}
                </p>
              ))
            }
            <p className="text-white/90">
              First Payment:{" "}
              <span className="font-medium">{formattedAmount}</span>
            </p>
          </div>

          {error && <p className="text-red-400">{error}</p>}

          <div className="pt-4">
            <Button
              text={loading ? "Processing..." : "Subscribe"}
              styles="w-[220px] h-[55px] text-lg tracking-wide font-semibold bg-secondary hover:bg-primary transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_15px_rgba(255,255,255,0.4)]"
              onClick={submitPayment}
              disabled={loading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriberCheckoutForm;
